// React
import { useState, useCallback } from "react";

// Third-party
import { CheckCircle } from "lucide-react";

// New Components
import { Box, Text } from "@/shared/ui/base";
import { Button, Modal } from "@/shared/ui/components";

// Components
import { VerificationStepper } from "./VerificationStepper";
import {
  VerificationStepOne,
  type StepOneData,
} from "./steps/VerificationStepOne";
import {
  VerificationStepTwo,
  type StepTwoData,
} from "./steps/VerificationStepTwo";

// Hooks
import { observer } from "mobx-react-lite";
import { useStore } from "@/stores";
import { useTranslation, useSnackbar } from "@/shared/hooks";

// API
import {
  submitVerification,
  type PartnerAccountType,
} from "@/shared/api/verificationApi";

/**
 * Props for VerificationModal
 */
interface VerificationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * VerificationModal Component
 *
 * Multi-step modal for partner verification:
 * 1. Registration form (account type, license, email)
 * 2. Document upload (deferred for media storage)
 * 3. Thank you screen
 */
export const VerificationModal = observer(function VerificationModal({
  open,
  onClose,
  onSuccess,
}: VerificationModalProps) {
  const t = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { authStore } = useStore();
  const { getIdToken } = authStore;

  // State
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  // Form data
  const [stepOneData, setStepOneData] = useState<StepOneData>({
    accountType: "corporate" as PartnerAccountType,
    companyName: "",
    licenseNumber: "",
    corporateEmail: "",
    officialWebsite: "",
  });

  const [stepTwoData, setStepTwoData] = useState<StepTwoData>({
    agencyLicenseUrl: "",
    officerIdUrl: "",
  });

  // Form errors
  const [stepOneErrors, setStepOneErrors] = useState<
    Partial<Record<keyof StepOneData, string>>
  >({});

  // Handle Step 1 field changes
  const handleStepOneChange = useCallback(
    (field: keyof StepOneData, value: string) => {
      setStepOneData((prev) => ({ ...prev, [field]: value }));
      // Clear error for this field
      setStepOneErrors((prev) => ({ ...prev, [field]: undefined }));
    },
    [],
  );

  // Handle Step 2 field changes
  const handleStepTwoChange = useCallback(
    (field: keyof StepTwoData, value: string) => {
      setStepTwoData((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  // Validate Step 1
  const validateStepOne = useCallback((): boolean => {
    const errors: Partial<Record<keyof StepOneData, string>> = {};

    if (!stepOneData.companyName || stepOneData.companyName.length < 2) {
      errors.companyName = "Company name is required (min 2 characters)";
    }

    if (!stepOneData.licenseNumber || stepOneData.licenseNumber.length < 10) {
      errors.licenseNumber = "License number is required (min 10 characters)";
    }

    if (
      !stepOneData.corporateEmail ||
      !stepOneData.corporateEmail.includes("@")
    ) {
      errors.corporateEmail = "Valid corporate email is required";
    }

    setStepOneErrors(errors);
    return Object.keys(errors).length === 0;
  }, [stepOneData]);

  // Navigate to next step
  const handleNext = useCallback(() => {
    if (currentStep === 1) {
      if (validateStepOne()) {
        setCurrentStep(2);
      }
    }
  }, [currentStep, validateStepOne]);

  // Navigate to previous step
  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  // Submit verification request
  const handleSubmit = useCallback(async () => {
    setSubmitting(true);

    try {
      const token = await getIdToken();
      if (!token) {
        showSnackbar(t("authFailed"), "error");
        return;
      }

      await submitVerification(token, {
        account_type: stepOneData.accountType,
        company_name: stepOneData.companyName,
        license_number: stepOneData.licenseNumber,
        corporate_email: stepOneData.corporateEmail,
        official_website: stepOneData.officialWebsite || null,
        documents: {
          agency_license_url: stepTwoData.agencyLicenseUrl || null,
          officer_id_url: stepTwoData.officerIdUrl || null,
        },
      });

      setShowThankYou(true);
      showSnackbar(t("verificationSubmitted"), "success");

      // Auto-close after 3 seconds
      setTimeout(() => {
        onSuccess();
      }, 3000);
    } catch (error) {
      console.error("Verification submission error:", error);
      showSnackbar(t("verificationSubmitError"), "error");
    } finally {
      setSubmitting(false);
    }
  }, [stepOneData, stepTwoData, getIdToken, showSnackbar, t, onSuccess]);

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Overlay>
        <Modal.Content size="md">
          {/* Header */}
          <Modal.Header>
            <Box className="flex items-center gap-6 flex-1">
              <Modal.Title>{t("verificationModalTitle")}</Modal.Title>
              {!showThankYou && (
                <VerificationStepper currentStep={currentStep} compact />
              )}
            </Box>
            {!submitting && !showThankYou && <Modal.Close />}
          </Modal.Header>

          {/* Content */}
          <Modal.Body>
            {!showThankYou ? (
              <>
                {/* Step Content */}
                {currentStep === 1 && (
                  <VerificationStepOne
                    data={stepOneData}
                    onChange={handleStepOneChange}
                    errors={stepOneErrors}
                  />
                )}

                {currentStep === 2 && (
                  <VerificationStepTwo
                    data={stepTwoData}
                    onChange={handleStepTwoChange}
                  />
                )}

                {/* Actions */}
                <Box className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                  <Button
                    variant="outline"
                    onClick={currentStep === 1 ? onClose : handleBack}
                    disabled={submitting}
                  >
                    {currentStep === 1
                      ? t("common.close")
                      : t("verificationBack")}
                  </Button>

                  {currentStep < 2 ? (
                    <Button
                      variant="primary"
                      onClick={handleNext}
                      disabled={submitting}
                    >
                      {t("verificationNext")}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting}
                      loading={submitting}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {t("verificationSubmit")}
                    </Button>
                  )}
                </Box>
              </>
            ) : (
              /* Thank You Screen */
              <Box className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <Text variant="h3" className="text-gray-900 mb-3">
                  {t("verificationThankYou")}
                </Text>
                <Text variant="body" className="text-gray-600 max-w-md mx-auto">
                  {t("verificationThankYouMessage")}
                </Text>
              </Box>
            )}
          </Modal.Body>
        </Modal.Content>
      </Modal.Overlay>
    </Modal.Root>
  );
});
